import { useState } from 'react';
import { TechnicalPlanMeta } from '@bulthaup/api/db';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  Box,
  Button,
  Group,
  Image,
  Select,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { MIME_TYPES } from '@mantine/dropzone';
import { useForm } from '@mantine/form';
import { IconBolt, IconPhoto } from '@tabler/icons-react';
import { zodResolver } from 'mantine-form-zod-resolver';
import { z } from 'zod';

import { trpc } from '../../../../../../services/trpc';
import { getUTDocumentUrl } from '../../../../../../services/upload';
import { DeletableImage } from './-deletable-image';
import { FileUploadButton } from './-file-upload-button';
import { SortableFileItem } from './-sortable-file-item';

export type TechnicalPlanFormValues = Omit<
  TechnicalPlanMeta,
  'technicalPlanTablePageCount'
>;

const schema = z.object({
  backsplash: z.string().min(1, 'La crédence est requise'),
  countertop: z.string().min(1, 'Le plan de travail est requis'),
  coverImagesUtFileKeys: z
    .array(z.string())
    .min(1, 'Au moins une photo est requise'),
  topViewsUtFiles: z
    .array(
      z.object({
        key: z.string(),
        name: z.string(),
        size: z.number(),
      }),
    )
    .min(1, 'Au moins une vue est requise'),
});

export function TechnicalPlanForm({
  onSubmit,
}: {
  projectId: string;
  onSubmit: (values: TechnicalPlanFormValues) => Promise<void>;
}) {
  const backsplashes = trpc.settings.get.useQuery({
    key: 'SPLASHBACK',
  });
  const countertops = trpc.settings.get.useQuery({
    key: 'WORKTOP',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<TechnicalPlanFormValues>({
    initialValues: {
      backsplash: '',
      countertop: '',
      observations: '',
      coverImagesUtFileKeys: [],
      topViewsUtFiles: [],
    },
    validate: zodResolver(schema),
  });

  const handleSubmit = async (values: typeof form.values) => {
    setIsSubmitting(true);
    try {
      await onSubmit(values);
    } finally {
      setIsSubmitting(false);
    }
  };

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const items = form.values.topViewsUtFiles;
      const oldIndex = items.findIndex(file => file.key === active.id);
      const newIndex = items.findIndex(file => file.key === over?.id);
      form.setFieldValue(
        'topViewsUtFiles',
        arrayMove(items, oldIndex, newIndex),
      );
    }
  }

  const mouseSensor = useSensor(MouseSensor);
  const touchSensor = useSensor(TouchSensor);
  const keyboardSensor = useSensor(KeyboardSensor);
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 0.01,
    },
  });

  const sensors = useSensors(
    pointerSensor,
    mouseSensor,
    touchSensor,
    keyboardSensor,
  );

  return (
    <form onSubmit={form.onSubmit(values => handleSubmit(values))}>
      <Stack gap="xl">
        <Stack gap="md">
          <Title order={4}>Informations</Title>
          <Group align="center">
            <Text w={120}>Crédence:</Text>
            <Select
              required
              w={250}
              placeholder="Sélectionnez une crédence"
              data={backsplashes.data?.value.map(b => ({
                label: b,
                value: b,
              }))}
              {...form.getInputProps('backsplash')}
            />
          </Group>

          <Group align="center">
            <Text w={120}>Plan de travail:</Text>
            <Select
              required
              w={250}
              placeholder="Sélectionnez un plan de travail"
              data={countertops.data?.value.map(c => ({
                label: c,
                value: c,
              }))}
              {...form.getInputProps('countertop')}
            />
          </Group>

          <Group align="start">
            <Text w={120}>Observations:</Text>
            <Textarea
              placeholder="Entrez vos observations"
              minRows={4}
              style={{ flex: 1 }}
              {...form.getInputProps('observations')}
            />
          </Group>
        </Stack>

        <Stack gap="md">
          <Title order={4}>Photos de page de garde</Title>

          <Group>
            <FileUploadButton
              leftSection={<IconPhoto size={16} />}
              placeholder="Ajouter des photos"
              accept={MIME_TYPES.jpeg}
              utUploadKey="technicalPlanCoverImagesUploader"
              multiple
              onChange={files =>
                form.setFieldValue('coverImagesUtFileKeys', [
                  ...form.values.coverImagesUtFileKeys,
                  ...files.map(f => f.key),
                ])
              }
              error={form.errors.coverImagesUtFileKeys}
            />
          </Group>
          {form.values.coverImagesUtFileKeys.length > 0 && (
            <Group gap="sm">
              {form.values.coverImagesUtFileKeys.map(key => (
                <DeletableImage
                  onDelete={() => {
                    form.setFieldValue(
                      'coverImagesUtFileKeys',
                      form.values.coverImagesUtFileKeys.filter(k => k !== key),
                    );
                  }}
                >
                  <Image
                    src={getUTDocumentUrl(key)}
                    height={70}
                    width="auto"
                    fit="contain"
                    style={{ maxHeight: '100px' }}
                  />
                </DeletableImage>
              ))}
            </Group>
          )}
        </Stack>

        <Stack gap="md">
          <Title order={4}>Plans et élévations</Title>
          <Group>
            <FileUploadButton
              placeholder="Ajouter des vues"
              accept={MIME_TYPES.pdf}
              utUploadKey="technicalPlanTopViewsUploader"
              multiple
              onChange={files => {
                form.setFieldValue('topViewsUtFiles', [
                  ...form.values.topViewsUtFiles,
                  ...files.map(f => ({
                    key: f.key,
                    name: f.name,
                    size: f.size,
                  })),
                ]);
              }}
              error={form.errors.topViewsUtFiles}
            />
          </Group>
          {form.values.topViewsUtFiles.length > 0 && (
            <Box mt="md">
              <Text fw={500} mb="xs">
                Plans et élévations ajoutés:
              </Text>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={form.values.topViewsUtFiles.map(file => file.key)}
                  strategy={verticalListSortingStrategy}
                >
                  <Stack gap="xs">
                    {form.values.topViewsUtFiles.map(file => (
                      <SortableFileItem
                        key={file.key}
                        id={file.key}
                        name={file.name}
                        onDelete={() => {
                          form.setFieldValue(
                            'topViewsUtFiles',
                            form.values.topViewsUtFiles.filter(
                              f => f.key !== file.key,
                            ),
                          );
                        }}
                      />
                    ))}
                  </Stack>
                </SortableContext>
              </DndContext>
            </Box>
          )}
        </Stack>

        <Group justify="center" mt="xl">
          <Button
            leftSection={<IconBolt size={16} />}
            type="submit"
            name="generate"
            size="md"
            loading={isSubmitting}
          >
            Générer le plan technique
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
